import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  IoAnalytics,
  IoBrush,
  IoChevronForward,
  IoFingerPrint,
  IoFlash,
  IoMusicalNotes,
  IoNotifications,
  IoPricetags,
} from "react-icons/io5";
import Main from "../../components/layout/Main";
import { useToggleTop } from "../../components/common/ToggleTop";
import { disablePushNotifications, enablePushNotifications, pushEnabled } from "../../firebase/push";
import { setAnalytics } from "../../preferences/analytics";
import { Settings } from "../../storage/store";
import { APP_SETTINGS_PATHS } from "./paths";
import "../../assets/styles/app-preferences.css";

// Why turning push on failed, by the step that failed (error.pushStep).
const PUSH_FAILURES = {
  worker: "This browser couldn't start the notification service. Please try again.",
  token:
    "This browser's push service refused to sign up for notifications. In Brave, turn on \"Use Google services for push messaging\" in Settings > Privacy and security.",
  server: "The HASHh server didn't accept this browser for notifications. Please try again.",
};

// Messages for the Push Notifications switch, by enablePushNotifications' result.
const PUSH_RESULTS = {
  enabled: ["Push notifications are on", "green"],
  blocked: ["Notifications are blocked for this site. Allow them in your browser's site settings.", "red"],
  dismissed: ["Allow notifications when your browser asks to turn them on", "blue"],
  "not-configured": ["Push notifications aren't set up for the dashboard yet", "blue"],
  unsupported: ["This browser doesn't support push notifications", "red"],
};

// A row that opens another screen.
const LinkRow = ({ icon: Icon, name, description, onOpen }) => (
  <li>
    <button type="button" className="hx-pref-row" onClick={onOpen}>
      <span className="hx-pref-name">
        <Icon aria-hidden="true" />
        {name}
      </span>
      <IoChevronForward className="hx-pref-go" aria-hidden="true" />
      {description ? <span className="hx-pref-description">{description}</span> : null}
    </button>
  </li>
);

// A row that is one switch, as the whole row toggles in the app.
const SwitchRow = ({ icon: Icon, name, description, checked, busy, onToggle }) => (
  <li>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-busy={busy || undefined}
      disabled={busy}
      className="hx-pref-row"
      onClick={() => onToggle(!checked)}
    >
      <span className="hx-pref-name">
        <Icon aria-hidden="true" />
        {name}
      </span>
      <span className={`hx-switch ${checked ? "is-on" : ""}`} aria-hidden="true">
        <span className="hx-switch-knob" />
      </span>
      {description ? <span className="hx-pref-description">{description}</span> : null}
    </button>
  </li>
);

// The app's Settings screen (app/screens/HomeSettingsScreen.js), opened from
// the side menu. The switches are kept in this browser, as the app keeps them
// on the phone.
function AppSettings() {
  const history = useHistory();
  const toast = useToggleTop();
  const [haptic, setHaptic] = useState(Settings.getHaptic);
  const [sound, setSound] = useState(Settings.getSound);
  const [analytics, setAnalyticsOn] = useState(Settings.getAnalytics);
  const [push, setPush] = useState(pushEnabled);
  const [busy, setBusy] = useState(null);
  const voltage = Settings.getHomeVoltage();

  const toggleHaptic = (next) => {
    Settings.setHaptic(next);
    setHaptic(next);
    if (next && !navigator.vibrate) toast("Haptic Feedback works in phone browsers that can vibrate", "blue");
  };

  const toggleSound = (next) => {
    Settings.setSound(next);
    setSound(next);
  };

  const toggleAnalytics = async (next) => {
    setBusy("analytics");
    try {
      const done = await setAnalytics(next);
      if (!done) {
        Settings.setAnalytics(false);
        toast("This browser can't share usage statistics", "red");
      }
    } catch (error) {
      Settings.setAnalytics(false);
      toast("Couldn't change Analytics. Please try again.", "red");
    } finally {
      setAnalyticsOn(Settings.getAnalytics());
      setBusy(null);
    }
  };

  const togglePush = async (next) => {
    setBusy("push");
    try {
      if (next) {
        const [message, color] = PUSH_RESULTS[await enablePushNotifications()];
        toast(message, color);
      } else {
        await disablePushNotifications();
        toast("Push notifications are off", "green");
      }
    } catch (error) {
      // Kept in the console so a failure can be looked into.
      console.error("Push notifications:", error);
      toast(
        (next && PUSH_FAILURES[error?.pushStep]) || `Couldn't turn ${next ? "on" : "off"} push notifications. Please try again.`,
        "red"
      );
    } finally {
      setPush(pushEnabled());
      setBusy(null);
    }
  };

  return (
    <Main header="stack" title="Settings" hideTabBar>
      <ul className="hx-pref">
        <LinkRow icon={IoBrush} name="Theme Settings" onOpen={() => history.push(APP_SETTINGS_PATHS.THEME)} />
        <SwitchRow
          icon={IoFingerPrint}
          name="Haptic Feedback"
          description="Vibrate on Device ON/OFF"
          checked={haptic}
          onToggle={toggleHaptic}
        />
        <SwitchRow
          icon={IoNotifications}
          name="Push Notifications"
          description="Get app notification"
          checked={push}
          busy={busy === "push"}
          onToggle={togglePush}
        />
        <SwitchRow
          icon={IoAnalytics}
          name="Analytics"
          description="Share Usage Statistics"
          checked={analytics}
          busy={busy === "analytics"}
          onToggle={toggleAnalytics}
        />
        <SwitchRow
          icon={IoMusicalNotes}
          name="Switch Sound"
          description="Sound on Device ON/OFF"
          checked={sound}
          onToggle={toggleSound}
        />
        <LinkRow
          icon={IoFlash}
          name="Home Voltage"
          description={voltage === "230" ? "Default 230V" : `${voltage}V`}
          onOpen={() => history.push(APP_SETTINGS_PATHS.VOLTAGE)}
        />
        <LinkRow icon={IoPricetags} name="Subscription" onOpen={() => history.push(APP_SETTINGS_PATHS.SUBSCRIPTION)} />
      </ul>
    </Main>
  );
}

export default AppSettings;
